import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper, Divider, Stack, TextField } from '@mui/material';
import { useTranslate, useLogout, Loading } from 'react-admin';
import { useTheme } from '@mui/material/styles';
import { useDashboardOnClickHandler } from './DashboardOnClickHandler';

const SectionHeader = ({ title }) => {
  const theme = useTheme();
  return (
    <Box
      sx={{
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        px: 3,
        py: 1.5,
        fontWeight: 700,
        letterSpacing: 1,
        textTransform: 'uppercase',
        textAlign: 'center',
        fontSize: '0.875rem',
      }}
    >
      {title}
    </Box>
  );
};

const DashboardSection = ({ title, children }) => {
  const theme = useTheme();
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 2,
        overflow: 'hidden',
        border: '1px solid #e0e0e0',
        backgroundColor: theme.palette.background.paper,
        boxShadow: '0px 4px 20px rgba(0,0,0,0.06)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SectionHeader title={title} />
      <Box sx={{ backgroundColor: '#f8f9fb', flexGrow: 1, p: 3 }}>{children}</Box>
    </Paper>
  );
};

const Card = ({ title, children }) => (
  <Paper
    elevation={0}
    sx={{
      borderRadius: 2,
      border: '1px solid #e0e0e0',
      backgroundColor: '#ffffff',
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}
  >
    {title && (
      <Box
        sx={{
          borderBottom: '1px solid #f0f0f0',
          px: 2.5,
          py: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ fontWeight: 700, letterSpacing: 0.5, color: '#4b5563', textTransform: 'uppercase', fontSize: '0.7rem' }}>
          {title}
        </Typography>
      </Box>
    )}
    <Box sx={{ flexGrow: 1, p: 2 }}>{children}</Box>
  </Paper>
);

const StatRow = ({ label, value, clickable, onClick }) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      py: 0.75,
      px: 1,
      borderBottom: '1px solid #f3f4f6',
      borderRadius: 1,
      cursor: clickable ? 'pointer' : 'default',
      transition: 'background-color 0.15s ease',
      '&:hover': { backgroundColor: clickable ? '#f5f5f5' : 'transparent' },
      '&:last-of-type': { borderBottom: 'none' },
    }}
    onClick={clickable ? onClick : undefined}
  >
    <Typography variant="body2" sx={{ color: '#374151', lineHeight: 1.4 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ fontWeight: 700, color: '#111827', ml: 2, whiteSpace: 'nowrap' }}>
      {value}
    </Typography>
  </Box>
);

const SumRow = ({ label = 'SUMA', value, onClick }) => (
  <>
    <Divider sx={{ my: 0.5 }} />
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'baseline',
        px: 1,
        py: 0.5,
        borderRadius: 1,
        cursor: onClick ? 'pointer' : 'default',
        '&:hover': { backgroundColor: onClick ? '#f5f5f5' : 'transparent' },
      }}
      onClick={onClick}
    >
      <Typography variant="caption" sx={{ fontWeight: 700, color: '#9ca3af', letterSpacing: 1 }}>
        {label}
      </Typography>
      <Typography variant="h6" sx={{ fontWeight: 700, color: '#C41430' }}>
        {value}
      </Typography>
    </Box>
  </>
);

const WadiaNwuCard = ({ title, wadia, nwu, total, totalLabel, onWadiaClick, onNwuClick, onTotalClick }) => (
  <Card title={title}>
    <Stack spacing={0.5}>
      <StatRow label="wadia" value={wadia} clickable={Boolean(onWadiaClick)} onClick={onWadiaClick} />
      <StatRow label="NWU" value={nwu} clickable={Boolean(onNwuClick)} onClick={onNwuClick} />
      <SumRow label={totalLabel || 'SUMA'} value={total} onClick={onTotalClick} />
    </Stack>
  </Card>
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const getYesterdayDate = () => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
};

const DashboardV2 = () => {
  const translate = useTranslate();
  const logout = useLogout();
  const { handleFieldClick } = useDashboardOnClickHandler();
  const [latestStats, setLatestStats] = useState(null);
  const [latestDate, setLatestDate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);
  const [selectedDate, setSelectedDate] = useState(getYesterdayDate());

  useEffect(() => {
    let isMounted = true;
    const controller = new AbortController();

    const fetchStats = async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('access_token');
        const params = new URLSearchParams({ date: selectedDate });
        const response = await fetch(`${API_URL}/dashboard/daily-stats/by-date/?${params}`, {
          headers: {
            Accept: 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: controller.signal,
        });

        if (response.status === 401) { logout(); return; }
        if (!response.ok) throw new Error(`HTTP ${response.status}`);

        const json = await response.json();
        if (!isMounted) return;
        setLatestStats(json?.data || null);
        setLatestDate(json?.date || null);
        setFetchError(null);
      } catch (error) {
        if (isMounted) setFetchError(error);
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    fetchStats();
    return () => { isMounted = false; controller.abort(); };
  }, [selectedDate]);

  // --- Data extraction ---
  const oferenciStats = latestStats?.oferenci || {};
  const marketingConsents = oferenciStats.zgody_marketingowe || {};
  const blokady = oferenciStats.blokady || {};
  const leadStatuses = oferenciStats.statusy_lead || {};
  const oferenciErrors = oferenciStats.errors || {};
  const oferenciArchive = oferenciStats.archiwum || {};

  // Zgody 24h big number
  const isLiveConsents = 'udzielone_zgody_24h' in marketingConsents;
  const consentValue = isLiveConsents
    ? (marketingConsents.udzielone_zgody_24h ?? 0)
    : (marketingConsents.udzielone_zgody ?? 0);
  const consentLabel = isLiveConsents
    ? 'ZGODY UZYSKANE PRZEZ OSTATNIE 24H'
    : `ZGODY UZYSKANE ${new Date(selectedDate + 'T00:00:00').toLocaleDateString('pl-PL')} 00:00 - 23:59`;

  const emailSum =
    (marketingConsents.udzielone_zgody ?? 0) +
    (marketingConsents.wyslane_emaile_oferta ?? 0) +
    (marketingConsents.wyslane_emaile ?? 0);

  // Blokady i wycofania — fallback to statusy_lead for old data
  const zablokowanych = blokady.zablokowanych_oferentow ?? leadStatuses.blokada_email ?? 0;
  const wycofaneZgody = blokady.wycofane_zgody ?? 0;
  const blokadyEmail = blokady.wystapienia_blokad_email ?? 0;
  const bezPwg = blokady.bez_danych_pwg ?? oferenciErrors.brak_pwg ?? 0;

  // Oferenci z błędem
  const errorsData = [
    { key: 'brak_nip', label: 'Brak NIP', value: oferenciErrors.brak_nip ?? 0 },
    { key: 'brak_email', label: 'Brak email', value: oferenciErrors.brak_email ?? 0 },
    { key: 'nie_znaleziono_w_gus', label: 'Nie znaleziono w GUS', value: oferenciErrors.nie_znaleziono_w_gus ?? 0 },
    { key: 'ai_console_error', label: 'AI Console Error', value: oferenciErrors.ai_console_error ?? 0 },
    { key: 'inne', label: 'Inne', value: oferenciErrors.inne ?? 0 },
  ];
  const errorsTotal = errorsData.reduce((s, i) => s + i.value, 0);

  // Archiwalni oferenci (simplified)
  const archiveData = [
    { label: 'Niespełnione PWG', value: oferenciArchive.niespelnione_pwg ?? 0 },
    { label: 'Niespełniony GUS', value: oferenciArchive.niespelniony_gus ?? 0 },
    { label: 'Nie uzyskano zgody marketingowej w ciągu 30 dni', value: oferenciArchive.brak_zgody_marketingowej ?? 0 },
  ];
  const archiveTotal = archiveData.reduce((s, i) => s + i.value, 0);

  // Zamówienia
  const zamowieniaStats = latestStats?.zamowienia || {};
  const pobrane = zamowieniaStats.pobrane || {};
  const zamErrors = zamowieniaStats.errors || {};
  const otwarcia = zamowieniaStats.otwarcia_ofert || {};
  const archiwizacja = zamowieniaStats.archiwum?.brak_wadium_nwu ?? 0;

  // Predykcje
  const predykcja = latestStats?.predykcja || {};
  const predZamowienia = predykcja.zamowienia || {};
  const predOferenci = predykcja.oferenci || {};

  const latestDateLabel = latestDate
    ? new Date(latestDate).toLocaleDateString('pl-PL')
    : null;

  if (loading) return <Loading />;

  return (
    <Box sx={{ backgroundColor: '#F5F5F5', minHeight: 'calc(100vh - 64px)' }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#333333' }}>
              {translate('resources.dashboard.heading', 'Statystyki')}
            </Typography>
            {latestDateLabel && (
              <Typography variant="body2" sx={{ color: '#666666', mt: 0.5 }}>
                {translate('resources.dashboard.latest_date', 'Dane z dnia')}: {latestDateLabel}
              </Typography>
            )}
          </Box>
          <TextField
            label={translate('resources.dashboard.select_date', 'Wybierz datę')}
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            inputProps={{ max: getYesterdayDate() }}
            sx={{ minWidth: 200, '& .MuiOutlinedInput-root': { backgroundColor: '#ffffff' } }}
          />
        </Box>
        {fetchError && (
          <Typography variant="body2" sx={{ color: '#b91c1c' }}>
            {translate('resources.dashboard.load_error', 'Nie udało się wczytać najnowszych statystyk.')}
          </Typography>
        )}
      </Box>

      <Grid container spacing={3}>
        {/* OFERENCI */}
        <Grid item xs={12} lg={8}>
          <DashboardSection title="Oferenci">
            <Grid container spacing={2}>

              {/* Row 1: Zgody 24h + Wysyłka e-mail */}
              <Grid item xs={12} sm={5}>
                <Card title={consentLabel}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', py: 2 }}>
                    <Typography variant="h2" sx={{ fontWeight: 700, color: '#C41430' }}>
                      {consentValue}
                    </Typography>
                  </Box>
                </Card>
              </Grid>
              <Grid item xs={12} sm={7}>
                <Card title="Wysyłka e-mail">
                  <StatRow label="Ilość udzielonych nowych zgód" value={marketingConsents.udzielone_zgody ?? 0} />
                  <StatRow label="Ilość wysłanych ofert do istniejących zgód" value={marketingConsents.wyslane_emaile_oferta ?? 0} />
                  <StatRow label="Ilość wysłanych emaili z prośbą o zgodę marketingową" value={marketingConsents.wyslane_emaile ?? 0} />
                  <SumRow value={emailSum} />
                </Card>
              </Grid>

              {/* Row 2: Blokady i wycofania | Wystąpienia i pozyskania */}
              <Grid item xs={12} sm={6}>
                <Card title="Blokady i wycofania">
                  <StatRow label="Ilość zablokowanych oferentów" value={zablokowanych} />
                  <StatRow label="Ilość wycofanych zgód marketingowych" value={wycofaneZgody} />
                  <StatRow label="Ilość wystąpień blokad wysyłki e-mail" value={blokadyEmail} />
                  <StatRow label="Ilość oferentów bez danych PWG" value={bezPwg} />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card title="Wystąpienia i pozyskania">
                  <StatRow label="Ilość pozyskanych nowych NIP" value={marketingConsents.nowe_nipy ?? 0} />
                  <StatRow label="Ilość wystąpień istniejących oferentów" value={marketingConsents.istniejacy_oferenci ?? 0} />
                  <StatRow label="Ilość wystąpień klientów Polfund" value={leadStatuses.klienci_polfund ?? 0} />
                </Card>
              </Grid>

              {/* Row 3: Oferenci z błędem | Archiwalni oferenci */}
              <Grid item xs={12} sm={6}>
                <Card title="Oferenci z błędem">
                  {errorsData.map((item) => (
                    <StatRow
                      key={item.key}
                      label={item.label}
                      value={item.value}
                      clickable={item.key !== 'ai_console_error' && item.key !== 'inne'}
                      onClick={() => handleFieldClick('oferenci_errors', item.key, false, selectedDate)}
                    />
                  ))}
                  <SumRow
                    value={errorsTotal}
                    onClick={() => handleFieldClick('oferenci_errors', undefined, true)}
                  />
                </Card>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Card title="Archiwalni oferenci">
                  {archiveData.map((item, idx) => (
                    <StatRow key={idx} label={item.label} value={item.value} />
                  ))}
                  <SumRow value={archiveTotal} />
                </Card>
              </Grid>

            </Grid>
          </DashboardSection>
        </Grid>

        {/* PRZETARGI */}
        <Grid item xs={12} lg={4}>
          <DashboardSection title="Przetargi">
            <Stack spacing={2}>

              <WadiaNwuCard
                title="Pobrane zamówienia"
                wadia={pobrane.wadia ?? 0}
                nwu={pobrane.nwu ?? 0}
                total={pobrane.all ?? ((pobrane.wadia ?? 0) + (pobrane.nwu ?? 0))}
                onWadiaClick={() => handleFieldClick('zamowienia_downloaded', 'wadia')}
                onNwuClick={() => handleFieldClick('zamowienia_downloaded', 'nwu')}
                onTotalClick={() => handleFieldClick('zamowienia_downloaded', undefined, true)}
              />

              {/* Ilość otwarć ofert — new section */}
              <Card title="Ilość otwarć ofert">
                <StatRow label="wadia" value={otwarcia.wadia ?? 0} />
                <StatRow label="NWU" value={otwarcia.nwu ?? 0} />
                <StatRow label="wadia i NWU" value={otwarcia.wadia_i_nwu ?? 0} />
                <SumRow value={otwarcia.all ?? 0} />
              </Card>

              <WadiaNwuCard
                title="Przetargi z błędami"
                wadia={zamErrors.wadia ?? 0}
                nwu={zamErrors.nwu ?? 0}
                total={zamErrors.all ?? ((zamErrors.wadia ?? 0) + (zamErrors.nwu ?? 0))}
                onWadiaClick={() => handleFieldClick('zamowienia_errors', 'wadia')}
                onNwuClick={() => handleFieldClick('zamowienia_errors', 'nwu')}
                onTotalClick={() => handleFieldClick('zamowienia_errors', undefined, true)}
              />

              <Card title="Archiwizacja">
                <StatRow label="Brak wadium/NWU" value={archiwizacja} />
              </Card>

            </Stack>
          </DashboardSection>
        </Grid>

        {/* PREDYKCJE */}
        <Grid item xs={12}>
          <DashboardSection title="Predykcje">
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <WadiaNwuCard
                  title="Nowe zamówienia"
                  wadia={predZamowienia.wadia ?? 0}
                  nwu={predZamowienia.nwu ?? 0}
                  total={predZamowienia.all ?? ((predZamowienia.wadia ?? 0) + (predZamowienia.nwu ?? 0))}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <WadiaNwuCard
                  title="Nowi Oferenci"
                  wadia={predOferenci.wadia ?? 0}
                  nwu={predOferenci.nwu ?? 0}
                  total={predOferenci.all ?? ((predOferenci.wadia ?? 0) + (predOferenci.nwu ?? 0))}
                />
              </Grid>
            </Grid>
          </DashboardSection>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DashboardV2;
